import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Card, CardBody, Spinner, Alert, Input, Label, Button, Badge, Collapse } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useAuth } from "auth/useAuth";
import { useCandidatos } from "./CandidatosContext";
import TableContainer from "../../components/Common/TableContainer";
import { useMediaQuery, MQ } from "../../shared/hooks/useMediaQuery";

const EDUCATION_LABELS = {
  elementary: "Ensino Fundamental",
  highschool: "Ensino Médio",
  college: "Ensino Superior",
  graduate: "Pós-Graduação",
};

const GENDER_LABELS = {
  masculino: "Masculino",
  feminino: "Feminino",
  outros: "Outro",
  prefiro_nao_informar: "Prefiro não informar",
};

// "YYYY-MM-DD" → age, by parts (new Date("YYYY-MM-DD") is UTC and is off by
// one day in BRT around birthdays).
const calcAge = (dateStr) => {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  const today = new Date();
  let age = today.getFullYear() - y;
  const monthDiff = today.getMonth() + 1 - m;
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d)) age--;
  return age;
};

const MOBILE_PAGE = 20;

const Candidatos = () => {
  document.title = "Candidatos | ACIG Guapiaçu Cidadão";

  const auth = useAuth();
  const navigate = useNavigate();
  const { candidates, loading, error, fetchCandidates } = useCandidatos();
  const isMobile = useMediaQuery(MQ.belowMd);

  // Filter state
  const [filterName, setFilterName] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [filterEducation, setFilterEducation] = useState("");
  const [filterGender, setFilterGender] = useState("");
  const [filterCurriculum, setFilterCurriculum] = useState(""); // "" | "with" | "without"
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [mobileShown, setMobileShown] = useState(MOBILE_PAGE);

  useEffect(() => {
    if (auth.isAuthenticated) fetchCandidates();
  }, [auth.isAuthenticated]);

  const cities = useMemo(() => {
    const set = new Set(candidates.map((c) => c.city).filter(Boolean));
    return [...set].sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [candidates]);

  const activeFilters = [filterName, filterCity, filterEducation, filterGender, filterCurriculum].filter(Boolean).length;

  const filteredData = useMemo(() => {
    return candidates.filter((c) => {
      const fullName = [c.firstName, c.lastName].filter(Boolean).join(" ").toLowerCase();
      if (filterName && !fullName.includes(filterName.toLowerCase())) return false;
      if (filterCity && c.city !== filterCity) return false;
      if (filterEducation && c.curriculum?.educationLevel !== filterEducation) return false;
      if (filterGender && c.gender !== filterGender) return false;
      if (filterCurriculum === "with" && !c.hasCurriculum) return false;
      if (filterCurriculum === "without" && c.hasCurriculum) return false;
      return true;
    });
  }, [candidates, filterName, filterCity, filterEducation, filterGender, filterCurriculum]);

  useEffect(() => { setMobileShown(MOBILE_PAGE); }, [filterName, filterCity, filterEducation, filterGender, filterCurriculum]);

  const clearFilters = () => {
    setFilterName("");
    setFilterCity("");
    setFilterEducation("");
    setFilterGender("");
    setFilterCurriculum("");
  };

  const columns = useMemo(
    () => [
      {
        header: "Nome",
        accessorFn: (row) => [row.firstName, row.lastName].filter(Boolean).join(" ") || "—",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Cidade",
        accessorKey: "city",
        cell: ({ getValue }) => getValue() || "—",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Idade",
        accessorFn: (row) => calcAge(row.dateOfBirth),
        cell: ({ getValue }) => getValue() != null ? getValue() : "—",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Celular",
        accessorKey: "cellphone",
        cell: ({ getValue }) => getValue() || "—",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        header: "Escolaridade",
        accessorFn: (row) => row.curriculum?.educationLevel || "",
        cell: ({ getValue }) => EDUCATION_LABELS[getValue()] || "—",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Currículo",
        accessorFn: (row) => (row.hasCurriculum ? 1 : 0),
        cell: ({ row }) => row.original.hasCurriculum
          ? <Badge color="success" pill>Preenchido</Badge>
          : <Badge color="light" pill className="text-muted border">Sem currículo</Badge>,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Experiências",
        accessorFn: (row) => row.curriculum?.experiences || [],
        cell: ({ getValue }) => {
          const exps = getValue();
          if (!exps || exps.length === 0) return "—";
          const visible = exps.slice(0, 3);
          const remaining = exps.length - 3;
          return (
            <div className="d-flex flex-wrap gap-1" style={{ maxWidth: 220 }}>
              {visible.map((exp) => (
                <Badge key={exp.id} color="info" className="font-size-11 fw-normal text-wrap">{exp.position}</Badge>
              ))}
              {remaining > 0 && (
                <Badge color="secondary" className="font-size-11 fw-normal" title={exps.slice(3).map((e) => e.position).join(", ")}>
                  +{remaining}
                </Badge>
              )}
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        header: "Ações",
        accessorKey: "id",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ getValue }) => (
          <Button color="info" size="sm" outline onClick={() => navigate(`/candidatos/${getValue()}`)}>
            <i className="bx bx-show me-1" />Detalhes
          </Button>
        ),
      },
    ],
    [navigate]
  );

  const filtersBody = (
    <Row className="g-3 align-items-end">
      <Col md={3}>
        <Label className="form-label" htmlFor="f-name">Nome</Label>
        <Input id="f-name" type="search" placeholder="Buscar por nome..." value={filterName} onChange={(e) => setFilterName(e.target.value)} />
      </Col>
      <Col xs={6} md={2}>
        <Label className="form-label" htmlFor="f-city">Cidade</Label>
        <Input id="f-city" type="select" value={filterCity} onChange={(e) => setFilterCity(e.target.value)}>
          <option value="">Todas</option>
          {cities.map((city) => <option key={city} value={city}>{city}</option>)}
        </Input>
      </Col>
      <Col xs={6} md={2}>
        <Label className="form-label" htmlFor="f-edu">Escolaridade</Label>
        <Input id="f-edu" type="select" value={filterEducation} onChange={(e) => setFilterEducation(e.target.value)}>
          <option value="">Todas</option>
          {Object.entries(EDUCATION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </Input>
      </Col>
      <Col xs={6} md={2}>
        <Label className="form-label" htmlFor="f-gender">Gênero</Label>
        <Input id="f-gender" type="select" value={filterGender} onChange={(e) => setFilterGender(e.target.value)}>
          <option value="">Todos</option>
          {Object.entries(GENDER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </Input>
      </Col>
      <Col xs={6} md={2}>
        <Label className="form-label" htmlFor="f-cur">Currículo</Label>
        <Input id="f-cur" type="select" value={filterCurriculum} onChange={(e) => setFilterCurriculum(e.target.value)}>
          <option value="">Todos</option>
          <option value="with">Com currículo</option>
          <option value="without">Sem currículo</option>
        </Input>
      </Col>
      <Col md={1} className="d-grid">
        <Button color="secondary" outline onClick={clearFilters} disabled={activeFilters === 0}>Limpar</Button>
      </Col>
    </Row>
  );

  const renderMobileCard = (c) => {
    const age = calcAge(c.dateOfBirth);
    const exps = c.curriculum?.experiences || [];
    return (
      <button
        key={c.id}
        type="button"
        className="list-card w-100 text-start bg-transparent p-3"
        onClick={() => navigate(`/candidatos/${c.id}`)}
        aria-label={`Ver detalhes de ${[c.firstName, c.lastName].filter(Boolean).join(" ") || "candidato"}`}
      >
        <div className="d-flex justify-content-between align-items-start gap-2">
          <div className="flex-grow-1">
            <div className="fw-semibold">{[c.firstName, c.lastName].filter(Boolean).join(" ") || "—"}</div>
            <div className="text-muted small">
              {[c.city, age != null ? `${age} anos` : null, EDUCATION_LABELS[c.curriculum?.educationLevel]].filter(Boolean).join(" · ") || "—"}
            </div>
            {exps.length > 0 && (
              <div className="d-flex flex-wrap gap-1 mt-2">
                {exps.slice(0, 3).map((exp) => <Badge key={exp.id} color="info" className="font-size-11 fw-normal">{exp.position}</Badge>)}
                {exps.length > 3 && <Badge color="secondary" className="font-size-11 fw-normal">+{exps.length - 3}</Badge>}
              </div>
            )}
          </div>
          <div className="text-end">
            {c.hasCurriculum
              ? <Badge color="success" pill>Currículo</Badge>
              : <Badge color="light" pill className="text-muted border">Sem currículo</Badge>}
            <div className="mt-2"><i className="bx bx-chevron-right font-size-20 text-muted" aria-hidden="true" /></div>
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h4 className="mb-0">Candidatos <span className="text-muted fw-normal font-size-14">({filteredData.length})</span></h4>
          <div className="d-flex gap-2">
            {isMobile && (
              <Button color={activeFilters ? "primary" : "secondary"} outline size="sm" onClick={() => setFiltersOpen((v) => !v)} aria-expanded={filtersOpen}>
                <i className="bx bx-filter-alt me-1" />Filtros{activeFilters ? ` (${activeFilters})` : ""}
              </Button>
            )}
            <Button color="primary" outline size="sm" onClick={() => fetchCandidates(true)} disabled={loading} aria-label="Atualizar lista">
              <i className={`bx bx-refresh ${isMobile ? "" : "me-1"} ${loading ? "bx-spin" : ""}`} />{!isMobile && "Atualizar"}
            </Button>
          </div>
        </div>

        {/* Filters: always visible on desktop, collapsible on phones */}
        {isMobile ? (
          <Collapse isOpen={filtersOpen}>
            <Card className="mb-3"><CardBody>{filtersBody}</CardBody></Card>
          </Collapse>
        ) : (
          <Card className="mb-3"><CardBody>{filtersBody}</CardBody></Card>
        )}

        {loading && candidates.length === 0 && (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        )}

        {error && <Alert color="danger">{error}</Alert>}

        {!loading && !error && filteredData.length === 0 && (
          candidates.length === 0 ? (
            <Alert color="info">Ainda não há candidatos cadastrados.</Alert>
          ) : (
            <Alert color="info" className="d-flex flex-wrap justify-content-between align-items-center gap-2">
              <span>Nenhum candidato corresponde aos filtros.</span>
              <Button color="info" outline size="sm" onClick={clearFilters}>Limpar filtros</Button>
            </Alert>
          )
        )}

        {!error && filteredData.length > 0 && (
          isMobile ? (
            <>
              <div>{filteredData.slice(0, mobileShown).map(renderMobileCard)}</div>
              {mobileShown < filteredData.length && (
                <div className="d-grid mt-3">
                  <Button color="secondary" outline onClick={() => setMobileShown((n) => n + MOBILE_PAGE)}>
                    Mostrar mais ({filteredData.length - mobileShown} restantes)
                  </Button>
                </div>
              )}
            </>
          ) : (
            <Card>
              <CardBody>
                <TableContainer
                  columns={columns}
                  data={filteredData}
                  isPagination={true}
                  isCustomPageSize={true}
                  isGlobalFilter={false}
                  tableClass="table-hover align-middle"
                  theadClass="table-light"
                  paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                  pagination="pagination"
                />
              </CardBody>
            </Card>
          )
        )}
      </Container>
    </div>
  );
};

export default Candidatos;
